import React from 'react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="bg-gray-950 text-white min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with PDF Download */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-400 mb-8">
            Last updated: June 8, 2025
          </p>
          <a 
            href="/BarnBills LLC Privacy Policy - Updated June 8, 2025.pdf" 
            download 
            className="inline-flex items-center space-x-2 brand-gradient text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-4-4m4 4l4-4m-6 4h8"></path>
            </svg>
            <span>Download PDF Version</span>
          </a>
        </div>
        
        {/* Privacy Policy Content */}
        <div className="prose prose-invert max-w-none text-gray-300">
          
          <p className="mb-8">
            BarnBills LLC ("BarnBills," "we," "us," or "our") respects your privacy. This Privacy
            Policy explains what information we collect, how we use it, and the choices you have.
          </p>

          {/* 1. Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Account Information</h3>
            <ul className="list-disc ml-6 space-y-1 mb-4">
              <li><strong>Data Elements:</strong> Name, email address</li>
              <li><strong>Source:</strong> Provided by you at account creation</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">Business Data</h3>
            <ul className="list-disc ml-6 space-y-1 mb-4">
              <li><strong>Data Elements:</strong> Horse names, owner names, invoice line items, totals</li>
              <li><strong>Source:</strong> Entered by you while using the Service</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">Usage Data</h3>
            <ul className="list-disc ml-6 space-y-1 mb-4">
              <li><strong>Data Elements:</strong> IP address, device type, browser, pages visited, timestamps</li>
              <li><strong>Source:</strong> Collected automatically via server logs</li>
            </ul>

            <p>
              No password hashes, payment–card numbers, or bank details are stored on BarnBills
              servers. Passwords are managed by Firebase Authentication, and all payments are
              processed by Stripe, which handles PCI–DSS compliance.
            </p>
          </section>

          {/* 2. How We Use Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Information</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>Operate and maintain the Service.</li>
              <li>Troubleshoot, secure, and improve functionality.</li>
              <li>Respond to support requests and communicate updates.</li>
              <li>Create aggregated, anonymized analytics (e.g., average invoice totals) that do not identify you.</li>
            </ul>
          </section>

          {/* 3. Legal Bases */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Legal Bases (GDPR reference)</h2>
            <p className="mb-3">For EU/EEA users, our legal bases are:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>(a) contract performance (providing the Service),</li>
              <li>(b) legitimate interests (Service security and improvement), and</li>
              <li>(c) consent (for optional communications).</li>
            </ul>
          </section>

          {/* 4. Sharing & Subprocessors */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Sharing & Subprocessors</h2>
            <p className="mb-4">
              We share information only with trusted subprocessors that enable the Service, each
              bound by written data–protection agreements:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse border border-gray-700">
                <thead>
                  <tr className="bg-gray-800">
                    <th className="border border-gray-700 px-4 py-2 text-left text-white">Subprocessor</th>
                    <th className="border border-gray-700 px-4 py-2 text-left text-white">Purpose</th>
                    <th className="border border-gray-700 px-4 py-2 text-left text-white">Location</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-700 px-4 py-2">Google Cloud Platform</td>
                    <td className="border border-gray-700 px-4 py-2">Hosting, database (Firestore)</td>
                    <td className="border border-gray-700 px-4 py-2">USA</td>
                  </tr>
                  <tr className="bg-gray-900">
                    <td className="border border-gray-700 px-4 py-2">Google Cloud Platform</td>
                    <td className="border border-gray-700 px-4 py-2">Hosting, database (Firestore) for EU/EEA clients (GDPR compliance)</td>
                    <td className="border border-gray-700 px-4 py-2">EU</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-700 px-4 py-2">Hybrid Hosting LLC</td>
                    <td className="border border-gray-700 px-4 py-2">Hosting (database functions, serverless backend, etc.)</td>
                    <td className="border border-gray-700 px-4 py-2">USA</td>
                  </tr>
                  <tr className="bg-gray-900">
                    <td className="border border-gray-700 px-4 py-2">Stripe, Inc.</td>
                    <td className="border border-gray-700 px-4 py-2">Payment processing</td>
                    <td className="border border-gray-700 px-4 py-2">USA</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-700 px-4 py-2">Google Workspace</td>
                    <td className="border border-gray-700 px-4 py-2">Email support</td>
                    <td className="border border-gray-700 px-4 py-2">USA</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p>We do not sell or rent personal information.</p>
          </section>

          {/* 5. Data Retention & Deletion */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Retention & Deletion</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Active Accounts:</strong> Data retained for the life of the account.</li>
              <li><strong>Account Deletion:</strong> Upon verified request, Customer Data is deleted within 30 days, except data required for legal or tax obligations.</li>
              <li><strong>Backups:</strong> Encrypted backups are kept for 30 days, then automatically purged.</li>
            </ul>
          </section>

          {/* 6. Security Measures */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Security Measures</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>Data encrypted in transit (TLS 1.2+) and at rest (AES–256).</li>
              <li>Multi–factor authentication enforced for administrative accounts.</li>
              <li>Role–based access controls and least–privilege principles.</li>
              <li>Annual penetration testing and continuous monitoring.</li>
            </ul>
          </section>

          {/* 7. Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Rights</h2>
            <p className="mb-3">Depending on your jurisdiction, you may have rights to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Access or export your data.</li>
              <li>Correct inaccuracies.</li>
              <li>Delete your data.</li>
              <li>Object to certain processing.</li>
              <li>Lodge a complaint with a supervisory authority.</li>
            </ul>
            <p className="mt-4">
              Submit requests to privacy@barnbills.com. We may verify identity before fulfilling.
            </p>
          </section>

          {/* 8. Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Children's Privacy</h2>
            <p>
              The Service is not directed to children under 13, and we do not knowingly collect
              personal data from children. If we learn we have collected such data, we will delete
              it promptly.
            </p>
          </section>

          {/* 9. Changes to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Changes to This Policy</h2>
            <p>
              We may update this Policy. Material changes will be emailed to registered users at
              least 15 days before taking effect. Continued use after the effective date constitutes
              acceptance.
            </p>
          </section>

          {/* 10. Contact Us */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. Contact Us</h2>
            <p className="mb-4">Questions about this Policy? Email legal@barnbills.com or mail:</p>
            <div className="ml-6">
              <p>BarnBills LLC<br />
              4721 Newtown Pike<br />
              Lexington, KY 40511<br />
              USA</p>
            </div>
          </section>

          <div className="mt-12 pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-500">End of Privacy Policy</p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage; 